import { centsToDollars, dollarsToCents } from "@birdwatch/common";
import { isNaN } from "lodash-es";

const usNumberFormat = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 0,
});

/**
 * Format a number with US locale (e.g. 3025 → "3,025").
 * Returns "" for null, undefined, or NaN.
 */
export const formatNumberUS = (
  value: number | string | null | undefined,
): string => {
  const n = Number(value);
  if (value == null || isNaN(n)) return "";
  return usNumberFormat.format(n);
};

export const formatCurrencyUSD = (
  amount: number | string | null | undefined,
  options: Intl.NumberFormatOptions = {},
) => {
  const numberAmount = Number(amount);
  if (isNaN(numberAmount)) {
    return "";
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    ...options,
  }).format(numberAmount);
};

/**
 * Formats a number as USD currency with compact notation (e.g., $1.2K, $3.4M).
 *
 * @param amount - The number to format as USD currency.
 * @returns A string representing the formatted currency in compact notation.
 */
export const formatCurrencyUSDCompact = (amount: number) => {
  return formatCurrencyUSD(amount, {
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });
};

export const formatToFixed = (input: any, toFixed = 2) => {
  if (!input) return "";
  return Number(input).toFixed(toFixed);
};

// The cents↔dollars conversion rule lives once, in @birdwatch/common
// (centsToDollars/dollarsToCents); these wrappers only add the webapp's
// null-means-zero convention. Note dollarsToCents rounds half-to-even
// (banker's), matching the money math used platform-side.
export const fromCents = (cents: number | null | undefined): number => {
  if (cents == null) return 0;
  return centsToDollars(cents);
};

export const formatCentsUSD = (
  cents: number | null | undefined,
  options: Intl.NumberFormatOptions = {},
) => {
  const dollars = fromCents(cents);
  // Whole dollars hide the decimal part ($10); any cents show exactly 2 ($10.11).
  // Skip defaults entirely when the caller already controls fraction digits, so
  // their explicit options can't clash with ours (Intl throws when min > max).
  const fractionDefaults =
    "minimumFractionDigits" in options || "maximumFractionDigits" in options
      ? {}
      : {
          minimumFractionDigits: dollars % 1 === 0 ? 0 : 2,
          maximumFractionDigits: 2,
        };
  return formatCurrencyUSD(dollars, { ...fractionDefaults, ...options });
};

/**
 * Like `formatCentsUSD`, but absent means absent: null/undefined renders the
 * em-dash placeholder instead of "$0.00". For report/detail surfaces where a
 * missing amount must not read as zero.
 */
export const formatCentsUSDOrDash = (
  cents: number | null | undefined,
  options: Intl.NumberFormatOptions = {},
): string => (cents == null ? "—" : formatCentsUSD(cents, options));

/**
 * Short form for headline amounts: exact below $1,000 ("$768.00"), then one
 * decimal with a K / M / B suffix ("$4.3K", "$276.7K", "$16.2M"). The short
 * form is rounded, so show the exact amount beside it (e.g. in a tooltip).
 */
export const formatCentsUSDShort = (cents: number | null | undefined) =>
  Math.abs(fromCents(cents)) < 1000
    ? formatCurrencyUSD(fromCents(cents))
    : formatCentsUSD(cents, {
        notation: "compact",
        minimumFractionDigits: 0,
        maximumFractionDigits: 1,
      });

export const toCents = (dollars: number | null | undefined): number => {
  if (dollars == null) return 0;
  return dollarsToCents(dollars);
};
